/* Alerte de temps anormal et signaux du navigateur (13/10/2026) : ce que le
 * recruteur voit. Le back calcule le niveau de rythme et les compteurs ; ici on
 * ne fait que choisir le texte. C'est un indice : il ne change ni le score ni le
 * verdict, et « aucun » ou « non évalué » n'affichent rien. */

const TITRES = {
  a_regarder: 'Rythme de réponse à regarder',
  inhabituel: 'Rythme de réponse inhabituel',
  // Dates identiques : probablement une donnee de demo ou une reprise, pas un comportement.
  donnees_anormales: 'Horodatage des réponses à vérifier',
}

const TITRE_SIGNAUX = 'Comportement pendant la passation à regarder'

export const NOTE_ALERTE_TEMPS = 'Simple indice : il ne modifie ni le score ni le verdict.'

/* PLANCHERS PROVISOIRES (13/10/2026), a fixer avec Nawal sur de vrais candidats.
   Sous ces valeurs on n'affiche rien : un candidat honnete change d'onglet une
   fois ou deux (une notification). */
export const PLANCHERS_SIGNAUX = Object.freeze({
  SORTIES_ONGLET: 3,
  COLLAGES: 1,
  VIDEOS_EN_PANNE: 1,
})

/* Date (ISO) de la mise en ligne des signaux. Un candidat qui a TERMINE apres cette date et dont
   aucun signal n'est arrive est signale (bloqueur de publicite, ancienne version en cache,
   panne) : « aucun signal » ne veut pas dire « rien a signaler ». `null` = pas encore pose, rien
   n'est affiche. ⚠️ A POSER AU DEPLOIEMENT. */
export const SIGNAUX_DEPUIS = '2026-10-13T12:49:00Z'

/* Les clics au meme endroit et les mouvements de souris sont COLLECTES mais pas montres : tant
   qu'ils ne sont pas calibres sur de vrais candidats, un double-clic ou une souris posee sur
   « Suivant » ferait passer un honnete homme pour un robot. */

const duree = (secondes) => {
  const s = Math.max(0, Math.round(secondes))
  if (s < 60) return `${s} s`
  const m = Math.floor(s / 60)
  return `${m} min${s % 60 ? ` ${s % 60} s` : ''}`
}
const fois = (n) => (n > 1 ? `${n} fois` : '1 fois')

/* `signaux` : un element de scan.signauxNavigateur (compteurs d'un candidat). */
export const faitsDuNavigateur = (signaux) => {
  if (!signaux) return []
  const faits = []
  if (signaux.sorties >= PLANCHERS_SIGNAUX.SORTIES_ONGLET) {
    faits.push(`A quitté l'onglet ${fois(signaux.sorties)} (${duree(signaux.horsOngletSecondes)} hors de l'écran au total).`)
  }
  if (signaux.collages >= PLANCHERS_SIGNAUX.COLLAGES) {
    faits.push(`A collé du texte ${fois(signaux.collages)}.`)
  }
  if (signaux.videosEnPanne >= PLANCHERS_SIGNAUX.VIDEOS_EN_PANNE) {
    faits.push(`La vidéo ne s'est pas lancée sur ${signaux.videosEnPanne > 1 ? `${signaux.videosEnPanne} écrans` : 'un écran'}.`)
  }
  return faits
}

const TITRE_MANQUE = 'Signaux de passation non reçus'

/* ─── INDICE DE VIGILANCE (13/10/2026, valide sur le principe par Hicham) ───────────────────────
 * Il COMBINE les signaux en un niveau lisible. Jamais un refus, jamais une note : il ne change ni le
 * score ni le verdict, et il n'est montre qu'au recruteur.
 * Un seul signal ne suffit JAMAIS a « eleve » (chaque famille vaut 2 points au plus, « eleve » en
 * demande 3) : il faut au moins deux familles de signaux differentes.
 * ⚠️ POINTS ET SEUILS PROVISOIRES, fixes sur UN humain et 8 passages d'IA : a calibrer avec Nawal sur de
 * vrais candidats. Les clics au meme endroit et la souris ne comptent PAS (collectes, non calibres). */
export const POINTS_VIGILANCE = Object.freeze({
  rythme: { inhabituel: 2, a_regarder: 1, donnees_anormales: 1 },
  onglet: [[10, 2], [3, 1]], // [sorties d'onglet >=, points], du plus haut au plus bas
  collage: [[5, 2], [1, 1]],
  video: [[1, 1]],
})
export const SEUILS_VIGILANCE = Object.freeze({ A_REGARDER: 1, ELEVE: 3 })

const pointsPar = (valeur, paliers) => (paliers.find(([seuil]) => (valeur || 0) >= seuil) || [0, 0])[1]

/* `alerte` : un element de scan.alertesTemps ; `signaux` : un element de scan.signauxNavigateur. */
export const indiceDeVigilance = (alerte, signaux) => {
  const facteurs = {
    rythme: POINTS_VIGILANCE.rythme[alerte?.niveau] || 0,
    onglet: pointsPar(signaux?.sorties, POINTS_VIGILANCE.onglet),
    collage: pointsPar(signaux?.collages, POINTS_VIGILANCE.collage),
    video: pointsPar(signaux?.videosEnPanne, POINTS_VIGILANCE.video),
  }
  const points = Object.values(facteurs).reduce((a, b) => a + b, 0)
  const niveau = points >= SEUILS_VIGILANCE.ELEVE ? 'eleve' : points >= SEUILS_VIGILANCE.A_REGARDER ? 'a_regarder' : 'aucun'
  return { points, niveau, facteurs: Object.keys(facteurs).filter((k) => facteurs[k] > 0) }
}

const TITRES_VIGILANCE = {
  eleve: 'Indice de vigilance\u00a0: élevé',
  a_regarder: 'Indice de vigilance\u00a0: à regarder',
}

/* Ce qui manque : le serveur n'a pas pu les calculer, ou rien n'est arrive d'un candidat qui a termine. */
export const manqueDeSignaux = (signaux, contexte = {}, depuis = SIGNAUX_DEPUIS) => {
  if (contexte.signauxDisponibles === false) return 'Les signaux du navigateur sont indisponibles pour le moment.'
  if (depuis && contexte.termineLe && new Date(contexte.termineLe) >= new Date(depuis) && !(signaux && signaux.ecrans > 0)) {
    return "Aucun signal reçu du navigateur de ce candidat. L'absence de signal n'est pas une preuve d'honnêteté."
  }
  return null
}

/* `alertes` : scan.alertesTemps ; `signaux` : scan.signauxNavigateur ; une entree par candidat du scan.
   `contexte` : { signauxDisponibles, termineLe } */
export const alerteDuCandidat = (alertes, userID, signaux, contexte = {}, depuis = SIGNAUX_DEPUIS) => {
  const alerte = (alertes || []).find((a) => a?.userID === userID)
  const titreTemps = TITRES[alerte?.niveau]
  const signauxCandidat = (signaux || []).find((x) => x?.userID === userID)
  const faits = faitsDuNavigateur(signauxCandidat)
  const manque = manqueDeSignaux(signauxCandidat, contexte, depuis)
  if (!titreTemps && faits.length === 0 && !manque) return null
  const vigilance = indiceDeVigilance(alerte, signauxCandidat)
  // Des dates anormales SEULES restent un probleme de donnees, pas un niveau de vigilance sur le candidat.
  const seulementDonnees = vigilance.facteurs.length === 1 && alerte?.niveau === 'donnees_anormales'
  const titre = (vigilance.niveau !== 'aucun' && !seulementDonnees)
    ? TITRES_VIGILANCE[vigilance.niveau]
    : (titreTemps || (faits.length > 0 ? TITRE_SIGNAUX : TITRE_MANQUE))
  return {
    niveau: vigilance.niveau,
    points: vigilance.points,
    titre,
    details: [...(titreTemps ? alerte.raisons || [] : []), ...faits, ...(manque ? [manque] : [])].map((f) => f.replace(/\.$/, '')),
    note: NOTE_ALERTE_TEMPS,
  }
}
